import { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import CardPhotoCarousel from 'src/components/elements/card-photo-carousel/CardPhotoCarousel'
import { formatPrice } from 'src/helpers/formatPrice'
import {
  ListingFactsParameters,
  getListingFacts,
  getPricePerSquareMeter,
} from 'src/helpers/listingFacts'
import { pricePeriodKey } from 'src/helpers/pricePeriod'
import { propertyTypeLabel } from 'src/helpers/propertyTypeLabel'

export interface IListingCard {
  id: string
  // Navigates to the listing; without it the card calls onOpen (a modal).
  href?: string
  onOpen?: () => void
  images: string[]
  alt: string
  price?: { amount: number; currency: string } | null
  listingType?: string | null
  propertyType?: string | null
  parameters?: ListingFactsParameters | null
  location?: string
  // grid: map, feeds and similar listings; compact: carousels
  variant?: 'grid' | 'compact'
  // Badges over the photo, top left (TOP, "your listing")
  topLeft?: ReactNode
  // Buttons over the photo, top right; they stop the click themselves
  actions?: ReactNode
  // Status chips under the location (price dropped, owner responded…)
  badges?: ReactNode
  selected?: boolean
  className?: string
}

// The one listing card of the site (F8). The map, the cabinet feeds, the
// matches of a request and the seller's other listings each had their own
// card: different widths and type, the price once "85 000 $" and once
// "2 500 000 ₴", the facts sometimes shown and sometimes not.
const ListingCard: FC<IListingCard> = ({
  id,
  href,
  onOpen,
  images,
  alt,
  price,
  listingType,
  propertyType,
  parameters,
  location,
  variant = 'grid',
  topLeft,
  actions,
  badges,
  selected,
  className,
}) => {
  const { t } = useTranslation()
  const compact = variant === 'compact'
  const typeLabel = propertyTypeLabel(t, propertyType ?? undefined)
  const facts = getListingFacts(t, { propertyType, parameters })
  const factsLine = [typeLabel, ...facts].filter(Boolean).join(' · ')
  const perSquareMeter = compact
    ? undefined
    : getPricePerSquareMeter(t, { listingType, price, parameters })
  const periodKey = pricePeriodKey(listingType)

  const body = (
    <>
      <div className="relative shrink-0">
        <CardPhotoCarousel
          images={images}
          alt={alt}
          className={compact ? 'w-full h-32' : 'w-full h-44'}
        />
        {topLeft && (
          <div className="absolute top-3 left-3 z-10 flex flex-col items-start gap-1">
            {topLeft}
          </div>
        )}
        {actions && (
          <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5">{actions}</div>
        )}
      </div>
      <div
        className={clsx('flex flex-col gap-1', compact ? 'px-3 pt-2.5 pb-3' : 'px-3.5 pt-3 pb-3.5')}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={clsx(
              'font-black text-ink leading-6 truncate',
              compact ? 'text-[1.0625rem]' : 'text-[1.1875rem]',
            )}
          >
            {price ? formatPrice(price.amount, price.currency) : ''}
            {periodKey && <span className="text-sm font-bold text-ink-2"> {t(periodKey)}</span>}
          </span>
          {perSquareMeter && (
            <span className="shrink-0 text-xs font-bold text-ink-2">{perSquareMeter}</span>
          )}
        </div>
        {factsLine && (
          <div className="text-sm font-bold text-ink truncate" title={factsLine}>
            {factsLine}
          </div>
        )}
        {location && (
          <div className="text-[0.8125rem] text-ink-2 truncate" title={location}>
            {location}
          </div>
        )}
        {badges && <div className="flex flex-wrap gap-1.5 pt-1">{badges}</div>}
      </div>
    </>
  )

  const classes = clsx(
    'group relative flex flex-col overflow-hidden bg-surface rounded-2xl shadow-card transition-shadow duration-200 hover:shadow-pop text-left',
    selected && 'ring-2 ring-brand',
    className,
  )

  if (href) {
    return (
      <Link
        to={href}
        onClick={onOpen}
        data-card="listing"
        data-property-id={id}
        className={classes}
      >
        {body}
      </Link>
    )
  }
  // A preview (the wizard shows how buyers will see it): nothing to open
  if (!onOpen) {
    return (
      <article data-card="listing" data-property-id={id} className={classes}>
        {body}
      </article>
    )
  }
  // Opens in place (a modal): the whole card takes the click, Enter or Space
  // on the focused card does the same for the keyboard.
  const open = (event: MouseEvent | KeyboardEvent) => {
    if ('key' in event) {
      // keys pressed on the buttons inside belong to those buttons
      if (event.target !== event.currentTarget) return
      if (event.key !== 'Enter' && event.key !== ' ') return
    } else if ((event.target as HTMLElement).closest('button, a, .MuiModal-root')) {
      // so do clicks on them (heart, compare) and on their menus and dialogs,
      // which React bubbles here through the portal
      return
    }
    event.preventDefault()
    onOpen?.()
  }
  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={[price ? formatPrice(price.amount, price.currency) : '', factsLine, location]
        .filter(Boolean)
        .join(', ')}
      onClick={open}
      onKeyDown={open}
      data-card="listing"
      data-property-id={id}
      className={clsx(
        classes,
        'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
      )}
    >
      {body}
    </article>
  )
}

export default ListingCard
